/* eslint-disable @typescript-eslint/ban-types */
import {
  BaseQueryFn,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryDefinition
} from '@reduxjs/toolkit/dist/query';

import {
  CrudFilter,
  CrudSort,
  MetaQuery,
  Pagination,
  DataProvider,
  GetListParams,
  GetListResponse
} from 'app/context/data/types';
import { BaseRecord } from 'app/context/data/types';
import { useResource } from '../resource/useResource';
import { pickDataProvider } from 'app/definitions/helpers/pickDataProvider';
import { useDataProvider } from './useDataProvider';
import {
  TypedUseQueryHookResult,
  UseQueryStateOptions
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';

// export interface UseListConfig {
//   pagination?: Pagination;
//   hasPagination?: boolean;
//   sort?: CrudSort[];
//   filters?: CrudFilter[];
// }

export type BaseListProps = {
  /**
   * Pagination properties
   */
  pagination?: Pagination;

  /**
   * Sorter parameters
   */
  sorters?: CrudSort[];
  /**
   * Filter parameters
   */
  filters?: CrudFilter[];
  /**
   * Meta data query for `dataProvider`
   */
  meta?: MetaQuery;
  /**
   * If there is more than one `dataProvider`, you should use the `dataProviderName` that you will use
   */
  dataProviderName?: string;
};

export type useListProps<
  TQueryFnData extends BaseRecord = BaseRecord,
  // TError extends HttpError = HttpError,
  TData extends BaseRecord = TQueryFnData
> = {
  resource?: string;
  queryOption?: SubscriptionOptions &
    UseQueryStateOptions<
      QueryDefinition<
        GetListParams,
        BaseQueryFn<
          GetListParams,
          any,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >,
        string,
        TData
      >,
      GetListResponse<TQueryFnData>
    >;
} & BaseListProps;

export const useList = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TData extends BaseRecord = TQueryFnData
>(
  props: useListProps<TQueryFnData, TData>
): TypedUseQueryHookResult<TData, GetListParams, any> => {
  const {
    resource: resourceFromProps,
    filters,
    sorters,
    pagination,
    meta,
    queryOption,
    dataProviderName
    //TODO: will Implement With Notification Provider
    // successNotification,
    // errorNotification,
  } = props;

  const { resources, resource, identifier } = useResource(resourceFromProps);
  const dataProvider: (dataProviderName?: string) => DataProvider =
    useDataProvider();
  const pickedDataProvider = pickDataProvider(
    identifier,
    dataProviderName,
    resources
  );
  const api = dataProvider(pickedDataProvider);
  const { useGetListQuery } = api;

  const query = useGetListQuery(
    {
      resource: resource.name ?? '',
      pagination: pagination,
      filters: filters,
      sorters: sorters,
      meta: meta
    },
    {
      ...queryOption,
      selectFromResult: (result) => {
        const { selectFromResult } = queryOption || {};
        if (selectFromResult) {
          return selectFromResult(result as any);
        }
        if (result?.data) {
          const data = result?.data;
          return {
            ...result,
            data: data?.data
          } as unknown as GetListResponse<TData>;
        }
        return {
          ...result,
          data: null
        } as unknown as GetListResponse<TData>;
      }
    }
  );

  return query as unknown as TypedUseQueryHookResult<TData, GetListParams, any>;
};
